import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

export default function JoinOffer() {
  const { audience } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/join-offers/${audience}`)
      .then((r) => setOffer(r.data))
      .catch(() => setOffer({ active: false }))
      .finally(() => setLoading(false));
  }, [audience]);

  if (audience !== "candidates" && audience !== "employers") return <Navigate to="/" replace />;
  if (loading) return <main className="max-w-3xl mx-auto px-4 py-24 text-center text-sm text-zinc-500" data-testid="join-loading">Loading…</main>;
  if (!offer?.active) return <Navigate to="/register" replace />;

  const isEmp = offer.role === "employer";
  const banner = isEmp ? "/email/banner-yellow.jpg" : "/email/banner-blue.jpg";
  const registerUrl = `/register?source=join-${offer.role}${isEmp ? "&role=employer" : ""}`;

  return (
    <main className="min-h-screen bg-zinc-50" data-testid="join-offer-page">
      <Seo title={`${offer.headline} | HiredToday`} description={offer.subtext} path={`/join/${audience}`} noindex />
      <div className="max-w-3xl mx-auto px-4 py-14 sm:py-20">
        <img src={banner} alt="HiredToday, The Search, Settled." data-testid="join-banner"
             className="w-full rounded-xl border border-zinc-200 shadow-sm" />
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-zinc-950 mt-10" data-testid="join-headline">
          {offer.headline}
        </h1>
        {offer.subtext && <p className="text-base md:text-lg text-zinc-600 mt-4 max-w-xl leading-relaxed" data-testid="join-subtext">{offer.subtext}</p>}

        {(offer.offer_label || offer.offer_detail) && (
          <div className="mt-8 bg-white border border-zinc-200 rounded-xl p-6 sm:p-8" data-testid="join-offer-card">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Your welcome offer</span>
            {offer.offer_label && <p className="font-display text-xl font-bold text-zinc-950 mt-3" data-testid="join-offer-label">{offer.offer_label}</p>}
            {offer.offer_detail && <p className="text-sm text-zinc-600 mt-2 leading-relaxed" data-testid="join-offer-detail">{offer.offer_detail}</p>}
          </div>
        )}

        <div className="mt-8 space-y-2.5">
          {(isEmp
            ? ["Post your roles to thousands of UK jobseekers", "Manage every applicant from one dashboard", "Verified company badge builds trust with candidates"]
            : ["Browse thousands of live UK roles, free forever", "Apply in minutes, your details go straight to the employer", "Verified badge makes your application stand out"]
          ).map((t) => (
            <p key={t} className="flex items-start gap-2.5 text-sm text-zinc-700"><CheckCircle2 size={16} className="text-blue-600 mt-0.5 shrink-0" />{t}</p>
          ))}
        </div>

        <Link to={registerUrl} data-testid="join-cta"
              className="mt-10 inline-flex items-center gap-2 h-12 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800 transition-colors duration-200">
          {offer.cta_label || "Create your free account"} <ArrowRight size={16} />
        </Link>
        <p className="text-xs text-zinc-500 mt-3">
          {isEmp ? "Free to join, you only pay when you post." : "Free for jobseekers, always."}
          {offer.promo_code ? " Your offer is applied to your account automatically when you join from this page." : ""}
        </p>
      </div>
    </main>
  );
}
